import axios from 'axios';

// Global API Cancellation Manager
class GlobalApiCancellationManager {
  constructor() {
    this.activeRequests = new Map();
    this.cancelTokenSource = null;
    this.isInitialized = false;
  }

  // Initialize the cancellation manager
  initialize() {
    if (this.isInitialized) return;
    
    // Initialize the cancellation manager
    
    // Store original axios methods
    const originalPost = axios.post;
    const originalGet = axios.get;
    const originalPut = axios.put;
    const originalDelete = axios.delete;
    const originalRequest = axios.request;
    
    // Override axios methods to add cancellation
    axios.post = (url, data, config = {}) => {
      return this.makeCancellableRequest(originalPost, url, data, config);
    };
    
    axios.get = (url, config = {}) => {
      return this.makeCancellableRequest(originalGet, url, config);
    };
    
    axios.put = (url, data, config = {}) => {
      return this.makeCancellableRequest(originalPut, url, data, config);
    };
    
    axios.delete = (url, config = {}) => {
      return this.makeCancellableRequest(originalDelete, url, config);
    };
    
    axios.request = (config) => {
      return this.makeCancellableRequest(originalRequest, config);
    };

    this.isInitialized = true;
  }

  // Make a request cancellable
  makeCancellableRequest(originalMethod, ...args) {
    // Generate unique request ID
    const requestId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Create cancel token for this specific request
    const source = axios.CancelToken.source();
    
    // Determine config object based on method signature
    let config = {};
    if (args.length === 1 && typeof args[0] === 'object' && !args[0].url) {
      // axios.request(config) format
      config = { ...args[0] };
    } else if (args.length >= 2) {
      // axios.post/get/put/delete(url, data/config, config) format
      config = args[args.length - 1] || {};
    }
    
    // Add cancellation token
    config.cancelToken = source.token;
    config.requestId = requestId;
    
    // Store the request
    this.activeRequests.set(requestId, source);
    
    // Update args with new config
    if (args.length === 1 && typeof args[0] === 'object' && !args[0].url) {
      args[0] = config;
    } else {
      args[args.length - 1] = config;
    }
    
    // Make the request
    return originalMethod.apply(axios, args)
      .then(response => {
        this.activeRequests.delete(requestId);
        console.log(`✅ Completed request: ${requestId}`);
        return response;
      })
      .catch(error => {
        this.activeRequests.delete(requestId);
        
        if (axios.isCancel(error)) {
          console.log(`🚫 Cancelled request: ${requestId}`);
        } else {
          console.log(`❌ Failed request: ${requestId}`);
        }
        throw error;
      });
  }

  // Cancel all active requests
  cancelAllRequests(reason = 'Page navigation') {
    console.log(`🚫 Cancelling ${this.activeRequests.size} active requests: ${reason}`);
    
    this.activeRequests.forEach((source, requestId) => {
      try {
        if (source && !source.token.reason) {
          source.cancel(reason);
          console.log(`✅ Cancelled: ${requestId}`);
        }
      } catch (error) {
        console.log(`⚠️ Error cancelling ${requestId}:`, error);
      }
    });
    
    this.activeRequests.clear();
    
    // Also try to cancel any remaining requests by clearing the map
    console.log(`🧹 Cleared ${this.activeRequests.size} remaining requests`);
  }

  // Get count of active requests
  getActiveRequestsCount() {
    return this.activeRequests.size;
  }

  // Get list of active requests
  getActiveRequests() {
    return Array.from(this.activeRequests.keys());
  }
}

// Create global instance
const globalApiCancellationManager = new GlobalApiCancellationManager();

// Export functions
export const initializeGlobalApiCancellation = () => {
  globalApiCancellationManager.initialize();
};

export const cancelAllGlobalRequests = (reason = 'Global cancellation') => {
  globalApiCancellationManager.cancelAllRequests(reason);
};

export const getActiveRequestsCount = () => {
  return globalApiCancellationManager.getActiveRequestsCount();
};

export const getActiveRequests = () => {
  return globalApiCancellationManager.getActiveRequests();
};

// Debug function to log current state
export const debugApiCancellation = () => {
  console.log('🔍 API Cancellation Debug Info:');
  console.log('- Initialized:', globalApiCancellationManager.isInitialized);
  console.log('- Active requests:', globalApiCancellationManager.getActiveRequestsCount());
  console.log('- Request IDs:', globalApiCancellationManager.getActiveRequests());
  return {
    initialized: globalApiCancellationManager.isInitialized,
    activeCount: globalApiCancellationManager.getActiveRequestsCount(),
    requests: globalApiCancellationManager.getActiveRequests()
  };
};

// Make debug function available globally for testing
if (typeof window !== 'undefined') {
  window.debugApiCancellation = debugApiCancellation;
  window.cancelAllGlobalRequests = cancelAllGlobalRequests;
}
